import { type FC, type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AIAgentIconButton, Button, Flex, Icon, Tooltip } from "@huub-web/ui";
import { useNavigationState } from "../../providers";
import { AdvisorAvatar, useAppointments } from "@huub-web/advisor";

import { FixedCompactNavigationHeaderButton } from "../FixedCompactNavigationHeaderButton/FixedCompactNavigationHeaderButton";
import { useAppController, useNavigate } from "../../hooks";

import {
  backButtonStyle,
  headerStyle,
  navStyle,
  pointerStyle,
  trailingContainerStyle,
} from "./Header.css";

export type HeaderProps = {
  /**
   * Additional trailing content rendered in the leading section.
   */
  trailing?: ReactNode;
  /**
   * Whether to show the header action buttons (back, talk-to-advisor, AI
   * agent). When false only the menu button remains. Defaults to true.
   */
  headerActions?: boolean;
};

/**
 * The main header for the Vite application.
 * Renders a sticky header with navigation controls, advisor link, and AI agent toggle.
 */
export const Header: FC<HeaderProps> = ({ trailing, headerActions = true }) => {
  const {
    setSideContentOpen,
    sideContentVariant,
    isSideContentOpen,
    menuVariant,
  } = useNavigationState();
  const { navigate, location } = useNavigate();
  const { isCustomLoading, enabledAppFlags } = useAppController();
  const { activeNavigationRecords, getNavigationPath } = useNavigationState();

  // Default to showing the button when the backend didn't send the flag list
  // (``null`` — older backends, or before the first state fetch). An empty
  // array is authoritative: no app flags are enabled, hide the button.
  const isTalkToAdvisorEnabled =
    enabledAppFlags === null || enabledAppFlags.includes("talkToAdvisor");

  const { t } = useTranslation();
  const { availableAdvisor } = useAppointments();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleTalkToAdvisor = () => {
    setSideContentOpen(false);
    navigate("/adviseur-spreken");
  };

  const toggleSideContent = () => {
    setSideContentOpen(!isSideContentOpen);
  };

  const hasMultipleLevels =
    activeNavigationRecords?.length && activeNavigationRecords.length > 1;
  const isNotRootPage = location.pathname !== "/";
  const hasBack = hasMultipleLevels || isNotRootPage;

  const handleBack = () => {
    if (!activeNavigationRecords?.length) {
      navigate("/");
      return;
    }

    const records = activeNavigationRecords
      .slice(0, -1)
      .map((record) => record.id) as string[];

    const path = getNavigationPath(...records);
    navigate(path || "/");
  };

  return (
    <header className={headerStyle}>
      <nav className={navStyle}>
        <Flex align="center" gap="xs" className={pointerStyle}>
          {trailing}
          {menuVariant === "compact" && <FixedCompactNavigationHeaderButton />}
          {headerActions && hasBack && (
            <Button
              variant="background"
              border="default"
              leading={<Icon icon="chevronLeft" size="s2" />}
              onClick={handleBack}
              className={backButtonStyle}
              disabled={isCustomLoading}
            >
              {t("back")}
            </Button>
          )}
        </Flex>

        <Flex justify="flex-end" gap="xs">
          <Flex gap="xs" justify="flex-end" className={trailingContainerStyle}>
            {headerActions && isTalkToAdvisorEnabled && (
              <Button
                size="s"
                variant="background"
                border="input"
                leading={availableAdvisor && <AdvisorAvatar size="s" />}
                onClick={handleTalkToAdvisor}
                alignSelf="flex-end"
                className={pointerStyle}
              >
                {t("advisor.speak")}
              </Button>
            )}
          </Flex>

          {headerActions && sideContentVariant === "compact" && (
            <Tooltip value={t("sideContent.open")} placement="left">
              <AIAgentIconButton
                ref={buttonRef}
                className={pointerStyle}
                onClick={toggleSideContent}
              />
            </Tooltip>
          )}
        </Flex>
      </nav>
    </header>
  );
};
